import React, { useState } from "react";
import { Button, Form, Row, Col, InputGroup } from "react-bootstrap";
import { Select } from "antd";
import { FaPencilAlt, FaTrash } from "react-icons/fa";
import swal from "sweetalert";
import "../medication/MedicationTable.css";

const { Option } = Select;

const CREATED_LABEL = { medicine: "Medicine", dose: "Dose", when: "When" };

export const ITEM_REQUIRED = [
  "medicine",
  "dose",
  "when",
  "durationValue",
];

const REQUIRED_LABELS = {
  medicine: "Medicine",
  dose: "Dose",
  when: "When",
  durationValue: "Duration",
};

export const emptyItemDraft = () => ({
  medicine: null,
  dose: "",
  when: "",
  durationValue: "",
  durationUnit: "Days",
  instructions: "",
});

export const missingFields = (draft) =>
  ITEM_REQUIRED.filter((field) => !draft || !draft[field]);

export const formatDuration = (row) => {
  if (!row) return "";

  // If durationValue is present, use it with unit
  if (row.durationValue) {
    return `${row.durationValue} ${row.durationUnit || "Days"}`;
  }

  // If duration field already contains the formatted value (e.g., "7 Days"), use it
  if (row.duration && /\d/.test(row.duration)) {
    return row.duration;
  }

  // If durationUnit is present, return it
  if (row.durationUnit) {
    return row.durationUnit;
  }

  // Fallback to duration field
  return row.duration || "";
};

const missingLabel = (missing) => {
  const names = missing.map((f) => REQUIRED_LABELS[f]);
  if (names.length === 1) return `Select a ${names[0]} to add`;
  return `Still needed: ${names.join(", ")}`;
};

const DURATION_UNITS = ["Days", "Weeks", "Months", "Years"];

export default function MedicineListItemsTable({
  title = "Medicines",
  savedRows = [],
  draft = emptyItemDraft(),
  editingItemId = null,
  medicines = [],
  doses = [],
  whens = [],
  loadingMedicines = false,
  loadingDoses = false,
  loadingWhens = false,
  category = "",
  onLoadMedicines,
  onLoadDoses,
  onLoadWhens,
  onCreateMedicine,
  onCreateDose,
  onCreateWhen,
  onDraftChange,
  onCommit,
  onCancelEdit,
  onEdit,
  onDelete,
  disabled = false,
  busy = false,
  allowAddNew = true,
}) {
  const [newItem, setNewItem] = useState({ medicine: "", dose: "", when: "" });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [creating, setCreating] = useState(null);

  const missing = missingFields(draft);
  // When editing (editingItemId is set), allow commit even if durationValue is missing
  // (user might not have entered it originally)
  const canCommit = (editingItemId !== null || missing.length === 0) && !disabled && !busy;

  const filterOption = (input, option) => {
    if (!input) return true;
    return option.children.toLowerCase().includes(input.toLowerCase());
  };

  const handleCreate = (field, creator) => {
    const typed = (newItem[field] || "").trim();
    if (!typed || !creator) return;

    setCreating(field);
    setOpenDropdown(null);

    Promise.resolve(creator(typed))
      .then((result) => {
        if (result) {
          onDraftChange(field, field === "medicine" ? result : result.name);
          setNewItem((prev) => ({ ...prev, [field]: "" }));
          if (field !== "medicine") {
            swal({
              title: "Success!",
              text: `${CREATED_LABEL[field]} "${typed}" added successfully`,
              icon: "success",
              buttons: false,
              timer: 2000,
            });
          }
        }
      })
      .finally(() => setCreating(null));
  };

  const notFound = (field, creator) =>
    newItem[field] ? (
      <div
        style={{
          padding: "5px",
          cursor: "pointer",
          background: "#f0f0f0",
          borderRadius: "4px",
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleCreate(field, creator);
        }}
      >
        Create "{newItem[field]}"
      </div>
    ) : (
      <div style={{ padding: "5px" }}>No data found</div>
    );

  const masterSelect = ({
    field,
    placeholder,
    options,
    loading,
    onLoad,
    creator,
    valueOf,
  }) => (
    <Select
      className="w-100"
      placeholder={placeholder}
      value={valueOf() || undefined}
      onChange={(value, option) => {
        // For medicine, pass the full object; for dose/when, pass just the name
        if (field === "medicine") {
          const selectedMedicine = options.find((opt) => opt.name === value);
          onDraftChange(field, selectedMedicine || { name: value });
        } else {
          onDraftChange(field, value);
        }
        setNewItem((prev) => ({ ...prev, [field]: "" }));
        setOpenDropdown(null);
      }}
      loading={loading || creating === field}
      showSearch
      open={openDropdown === field}
      onDropdownVisibleChange={(open) => {
        setOpenDropdown(open ? field : null);
        if (open && onLoad) onLoad();
      }}
      filterOption={filterOption}
      onSearch={(value) => setNewItem((prev) => ({ ...prev, [field]: value }))}
      notFoundContent={notFound(field, creator)}
      disabled={disabled}
    >
      {options.map((item) => (
        <Option key={item.id} value={item.name}>
          {item.name}
        </Option>
      ))}
    </Select>
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <strong className="mb-3">{title}</strong>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered border medication-table">
          <thead>
            <tr>
              <th>#</th>
              <th>MEDICINE</th>
              <th>DOSE</th>
              <th>WHEN</th>
              <th>DURATION</th>
              <th>INSTRUCTIONS</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {savedRows.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center text-muted">
                  No medicines added yet.
                </td>
              </tr>
            )}

            {savedRows.map((row, index) => {
              const itemId = row.medicineTagDetailId;
              const isBeingEdited =
                editingItemId !== null && editingItemId === itemId;

              return (
                <tr
                  key={`saved-${itemId || index}`}
                  className={isBeingEdited ? "table-warning" : ""}
                >
                  <td>{index + 1}</td>
                  <td>{row.medicineName || ""}</td>
                  <td>{row.doseName || ""}</td>
                  <td>{row.whenName || ""}</td>
                  <td>{row.durationValue ? `${row.durationValue} ${row.durationUnit || "Days"}` : (row.duration || formatDuration(row))}</td>
                  <td title={row.instructions || ""}>{row.instructions || "—"}</td>
                  <td className="text-nowrap" style={{ backgroundColor: "#e8e8e8", padding: "12px 8px", display: "flex", gap: "8px", alignItems: "center" }}>
                    <FaPencilAlt
                      title={
                        disabled
                          ? "Not available in read-only mode"
                          : isBeingEdited
                          ? "This row is open in the draft below"
                          : `Edit ${row.medicineName || "this medicine"}`
                      }
                      onClick={() =>
                        !disabled && !isBeingEdited && onEdit && onEdit(row)
                      }
                      style={{
                        cursor: disabled ? "not-allowed" : isBeingEdited ? "default" : "pointer",
                        color: disabled ? "#aaa" : isBeingEdited ? "#666" : "#0052cc",
                        fontSize: "20px",
                        padding: "4px",
                        display: "inline-block",
                        opacity: disabled ? 0.5 : 1,
                        pointerEvents: disabled ? "none" : "auto",
                      }}
                      aria-label={`Edit ${row.medicineName || "this medicine"}`}
                      role="button"
                      tabIndex={disabled ? -1 : 0}
                    />
                    <FaTrash
                      title={
                        disabled
                          ? "Not available in read-only mode"
                          : `Remove ${row.medicineName || "this medicine"}`
                      }
                      onClick={() =>
                        !disabled && onDelete && onDelete(row)
                      }
                      style={{
                        cursor: disabled ? "not-allowed" : "pointer",
                        color: disabled ? "#aaa" : "#cc0000",
                        fontSize: "20px",
                        padding: "4px",
                        display: "inline-block",
                        opacity: disabled ? 0.5 : 1,
                        pointerEvents: disabled ? "none" : "auto",
                      }}
                      aria-label={`Remove ${row.medicineName || "this medicine"}`}
                      role="button"
                      tabIndex={disabled ? -1 : 0}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!disabled && (editingItemId !== null || allowAddNew) && (
        <>
          <strong className="d-block mb-2">
            {editingItemId !== null ? "Edit Medicine" : "Add Medicine"}
            {category && (
              <span
                style={{
                  marginLeft: "12px",
                  fontSize: "14px",
                  fontWeight: "normal",
                  color: "#666",
                  backgroundColor: "#e8f4f8",
                  padding: "2px 8px",
                  borderRadius: "4px",
                  display: "inline-block",
                }}
              >
                Category: <strong>{category}</strong>
              </span>
            )}
          </strong>
          <Form onSubmit={(e) => e.preventDefault()}>
            <Row>
              <Col xs={12} sm={6} md={4} lg={2} className="mb-3 mb-lg-0">
                {masterSelect({
                  field: "medicine",
                  placeholder: "Select Medicine",
                  options: medicines,
                  loading: loadingMedicines,
                  onLoad: onLoadMedicines,
                  creator: onCreateMedicine,
                  valueOf: () => (draft.medicine ? draft.medicine.name : ""),
                })}
              </Col>
              <Col xs={12} sm={6} md={4} lg={2} className="mb-3 mb-lg-0">
                {masterSelect({
                  field: "dose",
                  placeholder: "Select Dose",
                  options: doses,
                  loading: loadingDoses,
                  onLoad: onLoadDoses,
                  creator: onCreateDose,
                  valueOf: () => draft.dose,
                })}
              </Col>
              <Col xs={12} sm={6} md={4} lg={2} className="mb-3 mb-lg-0">
                {masterSelect({
                  field: "when",
                  placeholder: "Select When",
                  options: whens,
                  loading: loadingWhens,
                  onLoad: onLoadWhens,
                  creator: onCreateWhen,
                  valueOf: () => draft.when,
                })}
              </Col>
              <Col xs={12} sm={6} md={4} lg={1} className="mb-3 mb-lg-0">
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={draft.durationValue || ""}
                    onChange={(e) =>
                      onDraftChange(
                        "durationValue",
                        e.target.value.replace(/[^0-9]/g, "")
                      )
                    }
                    placeholder="e.g., 20"
                  />
                  <Form.Control
                    as="select"
                    value={draft.durationUnit || "Days"}
                    onChange={(e) =>
                      onDraftChange("durationUnit", e.target.value)
                    }
                  >
                    {DURATION_UNITS.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </Form.Control>
                </InputGroup>
              </Col>
              <Col xs={12} md={8} lg={3} className="mb-3 mb-lg-0">
                <Form.Control
                  type="text"
                  value={draft.instructions || ""}
                  onChange={(e) =>
                    onDraftChange("instructions", e.target.value.substring(0, 100))
                  }
                  placeholder="Instructions"
                  maxLength={100}
                />
                <Form.Text className={(draft.instructions || "").length > 80 ? "text-danger text-right" : "text-muted text-right"}>
                  {(draft.instructions || "").length}/100
                </Form.Text>
              </Col>
              <Col xs={12} md={4} lg={1} className="d-flex flex-column">
                <span
                  title={canCommit ? "" : missingLabel(missing)}
                  className="d-inline-block"
                  style={{ width: "100%" }}
                >
                  <Button
                    variant="secondary"
                    className="btn-primary"
                    onClick={onCommit}
                    disabled={!canCommit}
                    style={!canCommit ? { pointerEvents: "none", width: "100%" } : { width: "100%" }}
                  >
                    {busy ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mr-1"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Save
                      </>
                    ) : editingItemId !== null ? (
                      "Update"
                    ) : (
                      "Add"
                    )}
                  </Button>
                </span>
                {editingItemId !== null && (
                  <Button
                    variant="link"
                    className="p-0 mt-1 d-block"
                    onClick={onCancelEdit}
                    disabled={busy}
                    style={{ fontSize: "12px" }}
                  >
                    Cancel
                  </Button>
                )}
              </Col>
            </Row>
          </Form>
          {missing.length > 0 && (
            <small className="text-muted">{missingLabel(missing)}.</small>
          )}
        </>
      )}
    </>
  );
}
